/**
 * Inline product image zoom.
 *
 * Click (desktop) or tap (mobile) on a product image to enter zoom mode.
 * While zoomed, mouse-move (desktop) or touch-move (mobile) pans a
 * high-resolution version of the image inside the existing
 * .product-gallery__media cell. Click/tap again — or mouse leaves the
 * cell on desktop — to exit zoom. No full-screen lightbox.
 *
 * Hi-res source is fetched lazily via Shopify CDN ?width=2400 so the
 * zoomed image is sharp without inflating initial PDP payload.
 */
(function () {
  'use strict';

  if (window.__productZoomInlineLoaded) return;
  window.__productZoomInlineLoaded = true;

  var ZOOM_WIDTH_PX = 2400;
  var ZOOM_SCALE_PERCENT = 250;
  var DESKTOP_MQ = '(min-width: 1000px)';
  var ZOOMING_CLASS = 'product-gallery__media--zooming';
  var ENHANCED_ATTR = 'data-inline-zoom-enhanced';

  function isImageMedia(cell) {
    return cell && cell.getAttribute('data-media-type') === 'image';
  }

  function getImage(cell) {
    return cell.querySelector(':scope > img');
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function buildHiResUrl(img) {
    if (img.dataset.zoomSrc) return img.dataset.zoomSrc;
    var base = img.currentSrc || img.src;
    if (!base) return '';
    var hiRes;
    if (/[?&]width=\d+/.test(base)) {
      hiRes = base.replace(/([?&])width=\d+/, '$1width=' + ZOOM_WIDTH_PX);
    } else {
      hiRes = base + (base.indexOf('?') > -1 ? '&' : '?') + 'width=' + ZOOM_WIDTH_PX;
    }
    img.dataset.zoomSrc = hiRes;
    return hiRes;
  }

  var hiResLoaded = new WeakSet();
  var hiResLoading = new WeakMap();

  function preloadHiRes(img) {
    if (hiResLoaded.has(img) || hiResLoading.has(img)) return hiResLoading.get(img);
    var url = buildHiResUrl(img);
    if (!url) return null;
    var promise = new Promise(function (resolve) {
      var pre = new Image();
      pre.onload = function () {
        hiResLoaded.add(img);
        resolve(url);
      };
      pre.onerror = function () {
        hiResLoading.delete(img);
        resolve(null);
      };
      pre.src = url;
    });
    hiResLoading.set(img, promise);
    return promise;
  }

  function setPosition(cell, clientX, clientY) {
    var rect = cell.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    var x = clamp(((clientX - rect.left) / rect.width) * 100, 0, 100);
    var y = clamp(((clientY - rect.top) / rect.height) * 100, 0, 100);
    cell.style.backgroundPosition = x + '% ' + y + '%';
  }

  var PENDING_CLASS = 'product-gallery__media--zoom-pending';

  function setLowResZoom(cell, img, clientX, clientY) {
    var rect = cell.getBoundingClientRect();
    var x = clamp(((clientX - rect.left) / rect.width) * 100, 0, 100);
    var y = clamp(((clientY - rect.top) / rect.height) * 100, 0, 100);
    img.style.transformOrigin = x + '% ' + y + '%';
    img.style.transform = 'scale(2.5)';
  }

  function clearLowResZoom(img) {
    img.style.transform = '';
    img.style.transformOrigin = '';
  }

  function applyHiResBackground(cell, img, hiRes, clientX, clientY) {
    cell.style.backgroundImage = 'url("' + hiRes + '")';
    setPosition(cell, clientX, clientY);
    cell.classList.remove(PENDING_CLASS);
    cell.classList.add(ZOOMING_CLASS);
    clearLowResZoom(img);
  }

  function activate(cell, img, clientX, clientY) {
    var hiRes = buildHiResUrl(img);
    if (!hiRes) return;

    if (hiResLoaded.has(img)) {
      applyHiResBackground(cell, img, hiRes, clientX, clientY);
      return;
    }

    // Cache miss: show low-res scale immediately for instant feedback,
    // then upgrade to hi-res background when the preload resolves.
    cell.classList.add(PENDING_CLASS);
    setLowResZoom(cell, img, clientX, clientY);

    var promise = preloadHiRes(img);
    if (!promise) return;
    promise.then(function (url) {
      if (!url) {
        cell.classList.remove(PENDING_CLASS);
        return;
      }
      // Only upgrade if still in pending state (user may have exited).
      if (!cell.classList.contains(PENDING_CLASS)) return;
      applyHiResBackground(cell, img, url, clientX, clientY);
    });
  }

  function deactivate(cell) {
    var img = getImage(cell);
    cell.classList.remove(ZOOMING_CLASS);
    cell.classList.remove(PENDING_CLASS);
    cell.style.backgroundImage = '';
    cell.style.backgroundPosition = '';
    if (img) clearLowResZoom(img);
  }

  function bindCell(cell) {
    if (!isImageMedia(cell)) return;
    if (cell.hasAttribute(ENHANCED_ATTR)) return;
    var img = getImage(cell);
    if (!img) return;
    cell.setAttribute(ENHANCED_ATTR, 'true');

    var desktopMql = window.matchMedia(DESKTOP_MQ);
    var pointerDown = null;

    // Eager preload on desktop hover — by the time the user actually
    // clicks, the hi-res image is already in the browser cache.
    cell.addEventListener('pointerenter', function (event) {
      if (event.pointerType === 'mouse') preloadHiRes(img);
    });

    // Distinguish click from drag/swipe so swiping the carousel doesn't
    // accidentally trigger zoom on release.
    cell.addEventListener('pointerdown', function (event) {
      if (event.target.tagName !== 'IMG') return;
      pointerDown = { x: event.clientX, y: event.clientY, type: event.pointerType };
      // Touch users skip hover, so kick off preload on first contact.
      if (event.pointerType !== 'mouse') preloadHiRes(img);
    });

    cell.addEventListener('pointerup', function (event) {
      if (event.target.tagName !== 'IMG') return;
      if (!pointerDown) return;
      var dx = Math.abs(event.clientX - pointerDown.x);
      var dy = Math.abs(event.clientY - pointerDown.y);
      var moved = dx > 8 || dy > 8;
      pointerDown = null;
      if (moved) return;
      if (cell.classList.contains(ZOOMING_CLASS)) {
        deactivate(cell);
      } else {
        activate(cell, img, event.clientX, event.clientY);
      }
    });

    // Desktop pan while zoomed.
    cell.addEventListener('mousemove', function (event) {
      if (!desktopMql.matches) return;
      if (!cell.classList.contains(ZOOMING_CLASS)) return;
      setPosition(cell, event.clientX, event.clientY);
    });

    // Auto-exit on mouse leave (desktop only — touch shouldn't auto-exit).
    cell.addEventListener('mouseleave', function () {
      if (!desktopMql.matches) return;
      deactivate(cell);
    });

    // Mobile pan while zoomed.
    cell.addEventListener('touchmove', function (event) {
      if (!cell.classList.contains(ZOOMING_CLASS)) return;
      if (!event.touches[0]) return;
      // Block carousel swipe while zoomed so the user can pan freely.
      event.preventDefault();
      setPosition(cell, event.touches[0].clientX, event.touches[0].clientY);
    }, { passive: false });

    // Reset zoom when carousel changes slides.
    var gallery = cell.closest('product-gallery');
    if (gallery) {
      var carousel = gallery.querySelector('.product-gallery__carousel');
      if (carousel) {
        carousel.addEventListener('carousel:change', function () {
          deactivate(cell);
        });
      }
    }
  }

  function enhanceGallery(gallery) {
    if (!gallery.hasAttribute('allow-zoom')) return;
    gallery.querySelectorAll('.product-gallery__media').forEach(bindCell);

    // Preload hi-res for the currently visible slide and for the next slide
    // the user lands on — keeps mobile tap-to-zoom feeling instant.
    var carousel = gallery.querySelector('.product-gallery__carousel');
    function preloadVisible(cell) {
      if (!cell || !isImageMedia(cell)) return;
      var img = getImage(cell);
      if (img) preloadHiRes(img);
    }
    if (carousel) {
      preloadVisible(carousel.querySelector('.product-gallery__media.is-initial'));
      carousel.addEventListener('carousel:settle', function (event) {
        preloadVisible(event && event.detail && event.detail.cell);
      });
    }
  }

  function neutralizeLightbox() {
    if (!window.customElements) return;
    window.customElements.whenDefined('product-gallery').then(function () {
      var PG = window.customElements.get('product-gallery');
      if (!PG || PG.__inlineZoomPatched) return;
      PG.__inlineZoomPatched = true;
      // No-op the lightbox open path. All three triggers (image click,
      // iOS pinch, mobile zoom button) dispatch 'lightbox:open' which
      // ultimately calls this method.
      PG.prototype.openLightBox = function () {};
      document.querySelectorAll('product-gallery').forEach(enhanceGallery);
    });

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('product-gallery')) {
            enhanceGallery(node);
          } else if (node.querySelectorAll) {
            node.querySelectorAll('product-gallery').forEach(enhanceGallery);
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', neutralizeLightbox);
  } else {
    neutralizeLightbox();
  }

  // Expose the scale value to CSS via a custom property so styling and
  // JS stay in sync.
  document.documentElement.style.setProperty('--product-zoom-bg-size', ZOOM_SCALE_PERCENT + '%');
})();
